import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronRight } from "lucide-react";
import { StrategyType } from "./investment-strategy/InvestmentStrategyContext";
import AltsPieChart from "./alts-allocation/AltsPieChart";
import LearnMoreAccordion from "./alts-allocation/components/LearnMoreAccordion";
import { useAltAllocations } from "./alts-allocation/hooks/useAltAllocations";
import { useToast } from "@/hooks/use-toast";
import { AltsAllocation } from "@/types/investment";

interface AltsAllocationSimulatorStepProps {
  onContinue: () => void;
  selectedStrategy?: StrategyType;
}

const AltsAllocationSimulatorStep = ({ onContinue, selectedStrategy }: AltsAllocationSimulatorStepProps) => {
  const { altAllocations, isLoading, handleAllocationsChange } = useAltAllocations(selectedStrategy);
  const { toast } = useToast();
  const [totalAllocation, setTotalAllocation] = useState(0);

  useEffect(() => {
    if (altAllocations) {
      const total = [
        altAllocations.private_equity_allocation,
        altAllocations.private_credit_allocation,
        altAllocations.private_debt_allocation,
        altAllocations.real_estate_allocation,
        altAllocations.hedge_funds_allocation,
        altAllocations.commodities_allocation,
        altAllocations.cryptocurrencies_allocation,
        altAllocations.collectibles_allocation
      ].reduce((sum, value) => sum + (value || 0), 0);
      
      setTotalAllocation(total);
    }
  }, [altAllocations]);

  return (
    <div className="space-y-4">
      <p className="text-navy-dark font-medium">
        Simulate different alternative investment allocations for your portfolio.
      </p>
      
      <div className="space-y-6">
        <LearnMoreAccordion 
          selectedStrategy={selectedStrategy}
          isLoading={isLoading}
          altAllocations={altAllocations}
        />

        <div className="mt-8 bg-white rounded-lg shadow-sm md:p-4 p-6">
          <h3 className="text-lg font-semibold text-navy-dark mb-4">
            Alternative Investments Allocation
          </h3>

          <AltsPieChart 
            altAllocations={altAllocations} 
            onAllocationsChange={handleAllocationsChange}
          />
        </div>

        <div className="pt-6">
          <Button 
            size="lg" 
            className="w-full max-w-md" 
            onClick={onContinue}
            disabled={totalAllocation !== 100}
          >
            Continue
            <ChevronRight className="ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AltsAllocationSimulatorStep;